"use client";

import React, { useState } from "react";
import { CircleCheck, Clock, MapPin, Phone, Mail, ArrowRight, ArrowLeft, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
const BUSINESS_NAME = "International Experiences";
const PHONE = "+1 (702) 366 6739\xA0";
const EMAIL = "theme.entertainment.talent@gmail.com";
const ADDRESS = "Global House";
const HOURS = "24/7 Global Support";
const WHAT_TO_EXPECT = ["Access to world-class musicians, dancers, and acrobats.", "Seamless global logistics and stage management.", "Elite talent curation tailored to your venue's vision.", "Transparent contracts with a focus on artistic excellence."];
export default function GetYourFreeEstimate() {
  return <section data-section-id="225016" id="start-the-conversation" className="bg-background py-16 lg:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-primary/5 -z-10 skew-y-3 transform origin-top-left" />

      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground tracking-widest uppercase">
                Let’s Create <span className="text-primary">Magic</span>
              </h2>
              <p className="text-lg text-muted-foreground font-sans max-w-md">
                The stage is set. Connect with our global talent scouts to bring world-class performers to your next production.
              </p>
            </div>

            <div className="space-y-6">
              <h3 className="text-xl font-default font-semibold text-primary border-b-2 border-primary/30 pb-2 inline-block tracking-wider">
                The Roster Experience
              </h3>
              <ul className="space-y-4">
                {WHAT_TO_EXPECT.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <span className="text-foreground font-sans">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-card border-2 border-border rounded-md p-6 shadow-xl space-y-4">
              <h3 className="text-lg font-default font-semibold text-foreground tracking-wide">Direct Booking Line</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="w-5 h-5 text-primary" />
                  <span className="font-sans font-medium text-foreground">{PHONE}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="w-5 h-5 text-primary" />
                  <span className="font-sans">{EMAIL}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="font-sans">{ADDRESS}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="font-sans">{HOURS}</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/10 translate-x-4 translate-y-4 rounded-md -z-10 border-2 border-primary/40 blur-sm" />
            <FormProvider formKey="start_conversation_form_secure_your_talent" formId="start_conversation_form_secure_your_talent" sectionName="get-your-free-estimate" defaultValues={{
            name: "",
            email: "",
            phone: "",
            address: "",
            service_type: "Musician",
            items_description: "",
            preferred_date: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              minLength: 10
            },
            address: {
              required: true
            },
            service_type: {
              required: true
            },
            items_description: {
              required: true,
              minLength: 10
            }
          }} submitText="Secure Talent" successMessage="The spotlight is waiting. Our agents will contact you shortly to finalize the details.">
              <EstimateMultiStepForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}
function EstimateMultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));
  const inputClasses = "flex h-11 w-full rounded-md border-b-2 border-t-0 border-x-0 border-border bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-4px_rgba(234,179,8,0.3)] disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";
  if (isSubmitted && !submitError) {
    return <Card className="border-2 border-primary shadow-2xl rounded-md overflow-hidden bg-card">
        <CardContent className="p-12 text-center space-y-6">
          <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
            <CircleCheck className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-2xl font-default font-bold text-foreground tracking-widest uppercase">Curtain Call!</h3>
          <p className="text-muted-foreground font-sans text-lg">{successMessage}</p>
          <Button onClick={() => {
          resetForm();
          setStep(1);
        }} variant="outline" className="mt-8 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            Submit Another Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="border-2 border-border shadow-2xl shadow-black/50 rounded-md overflow-hidden bg-card flex flex-col h-full">
      <Progress value={progress} className="h-1 bg-muted" />
      
      <CardHeader className="bg-muted/20 border-b-2 border-border pb-6 pt-8">
        <div className="flex justify-between items-center mb-2">
          <CardTitle className="text-2xl font-default text-foreground tracking-wide uppercase">
            {step === 1 && "Talent Category"}
            {step === 2 && "Performance Vision"}
            {step === 3 && "Producer Details"}
          </CardTitle>
          <span className="text-xs font-sans font-bold text-primary bg-primary/10 px-3 py-1 rounded-md border border-primary/20">
            ACT {step} OF {totalSteps}
          </span>
        </div>
        <CardDescription className="text-base font-sans text-muted-foreground">
          {step === 1 && "Select the type of star you wish to book."}
          {step === 2 && "Describe the stage, atmosphere, and requirements."}
          {step === 3 && "Provide your contact information for our agents."}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-8 pb-4 flex-grow">
        <Form onSubmit={handleSubmit} className="space-y-6">
          
          {}
          <div className={step === 1 ? "block space-y-6" : "hidden"}>
            <FormField control={control} name="service_type" rules={{
            required: "Please select a talent category"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-default uppercase tracking-widest text-primary">Category *</FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      {["Musician", "Singer", "Dancer", "Circus"].map((option, i) => <label key={option} data-index={i} className={`flex cursor-pointer items-center justify-center rounded-md border-2 p-4 text-xs font-bold uppercase tracking-tighter transition-all duration-300 hover:border-primary/50 ${field.value === option ? "border-primary bg-primary/10 text-primary shadow-[0_0_15px_rgba(234,179,8,0.2)]" : "border-border bg-background text-muted-foreground"}`}>
                          <input type="radio" className="sr-only" value={option} checked={field.value === option} onChange={field.onChange} />
                          {option}
                        </label>)}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="address" rules={{
            required: "Venue location is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-sm uppercase tracking-widest">Venue Location *</FormLabel>
                  <FormControl>
                    <input {...field} type="text" placeholder="e.g. Madison Square Garden, NYC" className={inputClasses} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="preferred_date" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-sm uppercase tracking-widest">Performance Date</FormLabel>
                  <FormControl>
                    <input {...field} type="date" className={inputClasses} />
                  </FormControl>
                  <FormDescription className="text-xs italic">Select the primary date of the event.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div className={step === 2 ? "block space-y-6" : "hidden"}>
            <FormField control={control} name="items_description" rules={{
            required: "Please describe your vision",
            minLength: {
              value: 10,
              message: "Please provide more artistic detail"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-sm uppercase tracking-widest text-primary">The Vision *</FormLabel>
                  <FormControl>
                    <textarea {...field} placeholder="Describe the performance requirements, stage size, and desired atmosphere..." className={`${inputClasses} min-h-[150px] py-3 resize-y border-2 rounded-md`} />
                  </FormControl>
                  <FormDescription className="text-xs">The more detail you provide, the better we can match the talent.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div className={step === 3 ? "block space-y-6" : "hidden"}>
            <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-sm uppercase tracking-widest">Full Name *</FormLabel>
                  <FormControl>
                    <input {...field} type="text" placeholder="Alexander Sterling" className={inputClasses} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-sm uppercase tracking-widest">Professional Email *</FormLabel>
                  <FormControl>
                    <input {...field} type="email" placeholder="producer@theatre.com" className={inputClasses} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-sm uppercase tracking-widest">Contact Number *</FormLabel>
                  <FormControl>
                    <input {...field} type="tel" placeholder="+1 (555) 000-0000" className={inputClasses} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="p-4 bg-destructive/10 border-2 border-destructive/20 rounded-md mt-6">
              <p className="text-destructive font-sans font-medium text-sm flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-lg bg-destructive inline-block" />
                {submitError}
              </p>
            </div>}

        </Form>
      </CardContent>

      <CardFooter className="bg-muted/5 border-t-2 border-border p-6 flex justify-between items-center mt-auto">
        {step > 1 ? <Button type="button" variant="outline" onClick={prevStep} className="border-2 border-border font-default uppercase tracking-widest text-xs">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button> : <div />}

        {step < totalSteps ? <Button type="button" onClick={nextStep} className="font-default bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs">
            Next Act
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button> : <Button type="submit" disabled={isSubmitting} className="font-default bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs min-w-[160px]" onClick={handleSubmit}>
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Booking...
              </> : 'Secure Talent'}
          </Button>}
      </CardFooter>
    </Card>;
}